type FormatLocale = 'de-AT' | 'en-GB'
type Language = 'de' | 'en'

const LANG_KEY = 'optimus.language'

// Initial aus dem localStorage lesen, damit schon der erste Render (vor
// jeglichem Effect) das richtige Zahlen-/Datumsformat zeigt.
function readLocale(): FormatLocale {
    try {
        return localStorage.getItem(LANG_KEY) === 'en' ? 'en-GB' : 'de-AT'
    } catch {
        return 'de-AT'
    }
}

let locale = readLocale()

let currency = makeCurrency()
let timeFmt = makeTime()
let weekdayShortFmt = makeWeekday('short')
let weekdayLongFmt = makeWeekday('long')
let monthYearFmt = makeMonthYear()

function makeCurrency() {
    return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: 'EUR',
    })
}
function makeWeekday(width: 'short' | 'long') {
    return new Intl.DateTimeFormat(locale, { weekday: width })
}
function makeMonthYear() {
    return new Intl.DateTimeFormat(locale, {
        month: 'long',
        year: 'numeric',
    })
}
function makeTime() {
    return new Intl.DateTimeFormat(locale, {
        hour: '2-digit',
        minute: '2-digit',
        // Niemals AM/PM — auch nicht, falls die Locale je wechselt.
        hourCycle: 'h23',
    })
}

/** Wird vom I18nProvider bei Sprachwechsel gerufen (de→de-AT, en→en-GB). */
export function setFormatLocale(language: Language) {
    const next: FormatLocale = language === 'de' ? 'de-AT' : 'en-GB'
    if (next === locale) return
    locale = next
    currency = makeCurrency()
    timeFmt = makeTime()
    weekdayShortFmt = makeWeekday('short')
    weekdayLongFmt = makeWeekday('long')
    monthYearFmt = makeMonthYear()
    monthNamesCache = null
}

export function formatMoney(value: number): string {
    return currency.format(value)
}

const pad2 = (n: number) => String(n).padStart(2, '0')

/**
 * Datum immer als dd.mm.yyyy — unabhängig von der Sprache (en-GB
 * renderte sonst Schrägstriche). Nur Wochentags-/Monatsnamen sind
 * übersetzt, Ziffernformate sind fix.
 */
export function formatDate(iso: string): string {
    const d = new Date(iso)
    return `${pad2(d.getDate())}.${pad2(d.getMonth() + 1)}.${d.getFullYear()}`
}

export function formatDateTime(iso: string): string {
    return `${formatDate(iso)}, ${formatTime(iso)}`
}

export function formatTime(iso: string): string {
    return timeFmt.format(new Date(iso))
}

/** Lokaler YYYY-MM-DD-Key → „dd.mm." (Monatsstunden-Tageszeilen). */
export function formatDateKeyShort(key: string): string {
    const [, m, d] = key.split('-')
    return `${d}.${m}.`
}

/** Wochentag-Kurzname (Mo/Mon) zum lokalen YYYY-MM-DD-Key. */
export function formatWeekdayShort(dateKey: string): string {
    return weekdayShortFmt.format(new Date(`${dateKey}T12:00:00`))
}

/** Wochentag-Name (Montag/Monday) zum lokalen YYYY-MM-DD-Key. */
export function formatWeekdayLong(dateKey: string): string {
    return weekdayLongFmt.format(new Date(`${dateKey}T12:00:00`))
}

/** „September 2026" — Monat ist 1-basiert. */
export function formatMonthYear(year: number, month: number): string {
    return monthYearFmt.format(new Date(year, month - 1, 15))
}

let monthNamesCache: string[] | null = null

/** Lokalisierte Monatsnamen (Index 0 = Januar). */
export function monthNames(): string[] {
    if (monthNamesCache === null) {
        monthNamesCache = Array.from({ length: 12 }, (_, i) =>
            monthYearFmt.formatToParts(new Date(2026, i, 15)).find(
                p => p.type === 'month',
            )?.value ?? String(i + 1),
        )
    }
    return monthNamesCache
}

/** 3661 → "01:01:01"; 3180 → "00:53:00" (Stunden unbeschränkt). */
export function formatClock(totalSeconds: number): string {
    const s = Math.max(0, Math.floor(totalSeconds))
    const h = Math.floor(s / 3600)
    const m = Math.floor((s % 3600) / 60)
    const sec = s % 60
    return [h, m, sec].map(n => String(n).padStart(2, '0')).join(':')
}

/** 3661 → "1:01 h"; 3180 → "53 min" — kompakt für Tabellen. */
export function formatDurationCompact(totalSeconds: number): string {
    const h = Math.floor(totalSeconds / 3600)
    const m = Math.round((totalSeconds % 3600) / 60)
    if (h === 0) return `${m} min`
    if (m === 0) return `${h} h`
    return `${h}:${String(m).padStart(2, '0')} h`
}

/** 28800 → "8,00 h" — Dezimalstunden für Soll/Ist-Tabellen (Arbeitszeit). */
export function formatHours(totalSeconds: number, decimals = 2): string {
    return `${(totalSeconds / 3600).toLocaleString(locale, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
    })} h`
}

/** Wie formatHours, aber mit explizitem Vorzeichen (Saldo-Spalten). */
export function formatHoursSigned(totalSeconds: number, decimals = 2): string {
    const value = totalSeconds / 3600
    const formatted = Math.abs(value).toLocaleString(locale, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
    })
    return `${value < 0 ? '−' : '+'}${formatted} h`
}

/** ISO-String → Wert für <input type="datetime-local"> (lokale Zeit). */
export function toLocalInputValue(iso: string): string {
    const d = new Date(iso)
    const pad = (n: number) => String(n).padStart(2, '0')
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

/** Wert aus <input type="datetime-local"> → ISO-String (UTC). */
export function fromLocalInputValue(value: string): string {
    return new Date(value).toISOString()
}

export function todayLocalInputValue(): string {
    return toLocalInputValue(new Date().toISOString()).slice(0, 10)
}

export function startOfWeekISO(): string {
    const d = new Date()
    const day = (d.getDay() + 6) % 7 // Montag = 0
    d.setDate(d.getDate() - day)
    d.setHours(0, 0, 0, 0)
    return d.toISOString()
}

export function startOfDayISO(): string {
    const d = new Date()
    d.setHours(0, 0, 0, 0)
    return d.toISOString()
}
